import React, { useState, useEffect } from 'react';
import { Modal, Button, Input, Select } from '@/components/ui';
import { Form, FormGroup, FormLabel } from '@/components/ui/Form';
import { useProjects } from '@/modules/projects/shared/hooks/useProjects';
import { useConstructionContracts } from '@/modules/contracts/construction/hooks/useConstructionContracts';
import type { ConstructionBoq } from '../types/construction-boq.types';

interface BoqFormModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (data: Partial<ConstructionBoq>) => Promise<void>;
  item?: ConstructionBoq | null;
  companyId: string;
}

export function BoqFormModal({ 
  isOpen, 
  onClose, 
  onSave, 
  item, 
  companyId 
}: BoqFormModalProps) {
  const [formData, setFormData] = useState<Partial<ConstructionBoq>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  const { projects } = useProjects(companyId);
  const { contracts } = useConstructionContracts(companyId, { 
    projectId: formData.project_id 
  });

  useEffect(() => {
    if (isOpen) {
      if (item) {
        setFormData({ ...item });
      } else {
        setFormData({
          company_id: companyId,
          boq_code: '',
          boq_name: '',
          status_code: 'draft',
          version_no: 1,
          total_amount: 0
        });
      }
    }
  }, [isOpen, item, companyId]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      await onSave(formData);
      onClose();
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Modal 
      isOpen={isOpen} 
      onClose={onClose} 
      title={item ? 'Cập nhật BOQ' : 'Khởi tạo BOQ mới'} 
      size="lg"
    >
      <Form onSubmit={handleSubmit}>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <FormGroup>
            <FormLabel required>Dự án</FormLabel>
            <Select
              value={formData.project_id || ''}
              onChange={(e) => setFormData({ ...formData, project_id: e.target.value })}
              options={projects.map(p => ({ value: p.id, label: p.name }))}
              required
            />
          </FormGroup>
          <FormGroup>
            <FormLabel>Hợp đồng liên quan</FormLabel>
            <Select
              value={formData.contract_id || ''}
              onChange={(e) => setFormData({ ...formData, contract_id: e.target.value })}
              options={contracts.map(c => ({ value: c.id, label: `${c.contract_code} - ${c.contract_name}` }))}
            />
          </FormGroup>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <FormGroup className="md:col-span-1">
            <FormLabel required>Mã BOQ</FormLabel>
            <Input 
              value={formData.boq_code || ''} 
              onChange={(e) => setFormData({ ...formData, boq_code: e.target.value })}
              placeholder="VD: BOQ-001"
              required
            />
          </FormGroup>
          <FormGroup className="md:col-span-2">
            <FormLabel required>Tên bảng khối lượng</FormLabel>
            <Input 
              value={formData.boq_name || ''} 
              onChange={(e) => setFormData({ ...formData, boq_name: e.target.value })}
              placeholder="VD: BOQ Thi công cảnh quan - Giai đoạn 1"
              required
            />
          </FormGroup>
        </div>

        <FormGroup>
          <FormLabel>Ghi chú / Mô tả</FormLabel>
          <textarea
            className="w-full rounded-xl border border-slate-200 focus:border-os-primary focus:ring-os-primary text-sm p-2.5 min-h-[100px] outline-none transition-all"
            value={formData.description || ''}
            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
            placeholder="Mô tả mục tiêu hoặc phạm vi của bảng BOQ này..."
          />
        </FormGroup>

        <div className="pt-4 flex justify-end gap-3 border-t border-slate-100">
          <Button variant="outline" type="button" onClick={onClose} disabled={isSubmitting}>
            Hủy
          </Button>
          <Button variant="primary" type="submit" isLoading={isSubmitting}>
            {item ? 'Lưu thay đổi' : 'Khởi tạo BOQ'}
          </Button>
        </div>
      </Form>
    </Modal>
  );
}
